import styled from "@emotion/styled";
import { css } from "@mui/material";
import { type PropsWithChildren, type ReactNode } from "react";

import type { ContextAction } from "../popup";
import { ContextActionsMenu } from "../popup";
import { ellipsise, FONT_WEIGHT_SEMI_BOLD, PANEL_HEADER_HEIGHT } from "../utilities";

export const PropertiesPanelFrame = styled.div`
  position: relative;
  height: 100%;

  border-left: 1px solid ${({ theme }) => theme.surfaceBack_1};
  background: ${({ theme }) => theme.surface};

  z-index: 1;
  max-width: 100%;
  width: auto;
  overflow-y: auto;

  transition: opacity 300ms ease-in-out;

  &.disabled {
    pointer-events: none;
    opacity: 0.3;
  }
`;

const PanelTitle = styled.div`
  ${css(ellipsise)};
  max-width: 100%;
  height: ${PANEL_HEADER_HEIGHT}px;
  line-height: ${PANEL_HEADER_HEIGHT}px;
  font-weight: ${FONT_WEIGHT_SEMI_BOLD};
  font-size: ${({ theme }) => theme.fontSize};
  padding-left: 4px;
`;

const PanelTitleWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  background-color: ${({ theme }) => theme.surfaceBack_2};
  color: ${({ theme }) => theme.generalTextIcon};
  max-width: 100%;
  padding: 0 8px;
`;

const PanelContainer = styled.div`
  display: flex;
  flex-direction: column;
  max-height: 100%;
  min-width: 180px;
  overflow: hidden;
`;

const ContentContainer = styled.div`
  max-height: 100%;
  min-width: 180px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
`;

type Props = {
  disabled?: boolean;
  title?: string;
  contextActions?: ContextAction[];
  disableKeyboardShortcuts?: boolean;
  children: ReactNode;
  hidden?: boolean;
};

export const PropertiesPanel = ({
  disabled,
  title = "Selected Item",
  contextActions = [],
  disableKeyboardShortcuts,
  hidden,
  children,
}: PropsWithChildren<Props>) => (
  <PropertiesPanelFrame className={disabled ? "disabled" : ""} style={hidden ? { display: "none" } : undefined}>
    <PanelContainer data-test="properties-panel-container">
      <PanelTitleWrapper data-test="header-container">
        <PanelTitle data-test="title">{title}</PanelTitle>
        <ContextActionsMenu contextActions={contextActions} disableKeyboardShortcuts={disableKeyboardShortcuts} />
      </PanelTitleWrapper>
      <ContentContainer data-test="content-container">{children}</ContentContainer>
    </PanelContainer>
  </PropertiesPanelFrame>
);
